export const WORKER_ROLE_BUCKETS = {
  supervisor: "supervisor",
  organizer: "organizer",
  offline: "offline",
};

/** A marker is offline only once the worker has checked out; every other
 * working slot status (break, pending check-out) still counts as online. */
export const isWorkerOffline = (marker) => marker?.online === false;

/** The worker's event role bucket, regardless of online/offline status.
 * The client portal omits `role` entirely, so anyone without a role falls
 * into the "organizer" bucket — the same split the API's counts use. */
export const getRoleBucket = (marker) => {
  const role = String(marker?.role?.name_en ?? marker?.role?.name ?? "").toLowerCase();
  return role.includes("supervisor")
    ? WORKER_ROLE_BUCKETS.supervisor
    : WORKER_ROLE_BUCKETS.organizer;
};

/** Offline workers are always bucketed as "offline" (grey), regardless of
 * their actual role, so markers/avatars visually distinguish online vs not. */
export const getWorkerRoleBucket = (marker) =>
  isWorkerOffline(marker) ? WORKER_ROLE_BUCKETS.offline : getRoleBucket(marker);

/** ~0.005deg is the real span of a 64px marker icon at zoom 14 around
 * lat 24.7 (156543m/px-at-eq * cos(lat) / 2^zoom -> meters/px -> degrees).
 * Anything smaller lets markers sit close enough on-screen to overlap
 * without being recognized as the same cluster. */
const BASE_CLUSTER_THRESHOLD_DEGREES = 0.005;
const BASE_CLUSTER_ZOOM = 14;

/** Converts the fixed clustering distance into the degree span that
 * corresponds to it at the map's current zoom, so markers that are close
 * in pixels merge into a cluster and automatically split apart as the
 * user zooms in (each zoom level halves the degrees-per-pixel ratio). */
export const getClusterThresholdForZoom = (zoom) =>
  BASE_CLUSTER_THRESHOLD_DEGREES * 2 ** (BASE_CLUSTER_ZOOM - (zoom ?? BASE_CLUSTER_ZOOM));

/** The marker icon's on-screen size, in the same pixel space that
 * BASE_CLUSTER_THRESHOLD_DEGREES was measured against. */
const MARKER_ICON_PIXELS = 64;

/** Converts an on-screen pixel distance into the degree span it covers at
 * the given zoom, so spreads stay a constant size on screen instead of
 * ballooning past the viewport at high zoom. */
const pixelsToDegrees = (pixels, zoom) =>
  (pixels * BASE_CLUSTER_THRESHOLD_DEGREES) / MARKER_ICON_PIXELS *
  2 ** (BASE_CLUSTER_ZOOM - (zoom ?? BASE_CLUSTER_ZOOM));

const getClusterCentroid = (members) => ({
  lat: members.reduce((sum, member) => sum + member.lat, 0) / members.length,
  lng: members.reduce((sum, member) => sum + member.lng, 0) / members.length,
});

/** Groups markers that sit within `thresholdDegrees` of each other so
 * overlapping pins render as a single clustered marker with a count badge.
 * Repeatedly merges any two clusters that end up within range of each
 * other's centroid (not just of the first marker that started a cluster),
 * so nearby clusters don't render as separate, overlapping pins. */
export const groupNearbyMarkers = (markers, thresholdDegrees = BASE_CLUSTER_THRESHOLD_DEGREES) => {
  let clusters = markers.map((marker) => ({ lat: marker.lat, lng: marker.lng, members: [marker] }));

  let merged = true;
  while (merged) {
    merged = false;
    for (let i = 0; i < clusters.length && !merged; i += 1) {
      for (let j = i + 1; j < clusters.length; j += 1) {
        if (
          Math.abs(clusters[i].lat - clusters[j].lat) < thresholdDegrees &&
          Math.abs(clusters[i].lng - clusters[j].lng) < thresholdDegrees
        ) {
          const members = [...clusters[i].members, ...clusters[j].members];
          clusters[i] = { ...getClusterCentroid(members), members };
          clusters = clusters.filter((_, index) => index !== j);
          merged = true;
          break;
        }
      }
    }
  }

  return clusters;
};

/** Clusters larger than this zoom the map in on click (rather than opening
 * the tooltip directly), so the tooltip list never has to show more than
 * this many members at once. */
export const CLUSTER_SPLIT_THRESHOLD = 6;

/** Past this zoom level, a cluster is broken open into a ring even if its
 * members' raw coordinates are still too close to split by distance alone
 * (manual admin check-ins all land on the event's own coordinates, so their
 * pins are exactly stacked and no amount of zoom separates them). */
export const AUTO_ALIGN_ZOOM = 18;

/** How many times a stack has been broken open: every zoom level past the
 * align threshold divides it one level finer. */
const getAlignDepthForZoom = (zoom) => Math.max(0, (zoom ?? 0) - AUTO_ALIGN_ZOOM);

/** How many sub-clusters a stack breaks into per zoom level. Deliberately
 * small so a big stack steps down through medium groups (20 -> 7+7+6 ->
 * 3s) instead of jumping straight to its final pins in one go. */
const CLUSTER_BRANCH_FACTOR = 3;

/** Splits members into evenly-sized chunks that get finer with each zoom
 * level, so a 20-stack walks down 7+7+6 -> 3s -> individual pins rather
 * than dumping all 20 on top of each other the moment the ring opens. */
const chunkMembers = (members, depth) => {
  const groupCount = Math.min(members.length, CLUSTER_BRANCH_FACTOR ** (depth + 1));
  const chunkSize = Math.ceil(members.length / groupCount);
  const chunks = [];
  for (let i = 0; i < members.length; i += chunkSize) {
    chunks.push(members.slice(i, i + chunkSize));
  }
  return chunks;
};

/** The ring has to be big enough to seat every pin on its circumference
 * without them touching, so the radius grows with the number of pins. */
const getRingRadiusPixels = (count) =>
  Math.max(MARKER_ICON_PIXELS, (count * MARKER_ICON_PIXELS * 1.15) / (2 * Math.PI));

/** The zoom level a clicked cluster should jump to: one level in when that
 * actually pulls the group apart, otherwise straight to the align zoom.
 * Members stacked on identical coordinates (every manual admin check-in
 * sits on the event's own point) never separate by distance, so stepping
 * one level at a time would look like the click did nothing. */
export const getClusterClickZoom = (cluster, zoom) => {
  const nextZoom = (zoom ?? BASE_CLUSTER_ZOOM) + 1;
  const splitsByDistance =
    groupNearbyMarkers(cluster.members, getClusterThresholdForZoom(nextZoom)).length > 1;
  return splitsByDistance ? nextZoom : Math.max(nextZoom, AUTO_ALIGN_ZOOM);
};

/** Decides how one cluster renders at the current zoom: a single numbered
 * pin below the align threshold, otherwise a ring of smaller sub-clusters
 * (each individually clickable) that subdivide further as you keep zooming. */
export const expandClusterForZoom = (cluster, zoom) => {
  if (cluster.members.length <= 1 || zoom < AUTO_ALIGN_ZOOM) return [cluster];

  const chunks = chunkMembers(cluster.members, getAlignDepthForZoom(zoom));
  if (chunks.length <= 1) return [cluster];

  const radius = pixelsToDegrees(getRingRadiusPixels(chunks.length), zoom);
  return chunks.map((members, index) => {
    const angle = (2 * Math.PI * index) / chunks.length;
    return {
      lat: cluster.lat + radius * Math.cos(angle),
      lng: cluster.lng + radius * Math.sin(angle),
      members,
    };
  });
};
